import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useBusiness } from "../context/BusinessContext";
import { fmtPrice } from "../lib/api";
import { Minus, Plus, X } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, total, count } = useCart();
  const { business } = useBusiness();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="cart-empty">
        <div className="text-xs uppercase tracking-ultra text-neutral-500">Your cart is</div>
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter mt-4">Empty.</h1>
        <p className="mt-6 text-neutral-600">Every great story begins with a first page.</p>
        <Link
          to="/shop"
          className="mt-8 inline-block bg-black text-white px-8 py-4 text-xs uppercase tracking-ultra font-bold"
          data-testid="cart-start-shopping"
        >
          Start shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cart-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-ultra text-neutral-500">Cart</div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mt-2">
            {count} {count === 1 ? "item" : "items"}
          </h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-12">
        <div className="border-t border-black">
          {items.map((item) => (
            <div
              key={item.product_id}
              className="grid grid-cols-[96px_1fr_auto] gap-6 items-center py-6 border-b border-black/10"
              data-testid={`cart-row-${item.product_id}`}
            >
              <div className="w-24 h-24 bg-neutral-200 overflow-hidden">
                {item.image_url && (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div>
                <div className="text-sm font-medium">{item.name}</div>
                <div className="text-xs uppercase tracking-ultra text-neutral-500 mt-1">
                  {fmtPrice(item.price, business.currency)}
                </div>
                <div className="mt-3 inline-flex items-center border border-black">
                  <button
                    onClick={() => updateQty(item.product_id, item.quantity - 1)}
                    className="p-2 hover:bg-black hover:text-white"
                    data-testid={`cart-dec-${item.product_id}`}
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="px-4 text-sm font-bold tabular-nums">{item.quantity}</span>
                  <button
                    onClick={() => updateQty(item.product_id, item.quantity + 1)}
                    className="p-2 hover:bg-black hover:text-white"
                    data-testid={`cart-inc-${item.product_id}`}
                    disabled={item.quantity >= item.stock}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold">
                  {fmtPrice(item.price * item.quantity, business.currency)}
                </div>
                <button
                  onClick={() => removeItem(item.product_id)}
                  className="mt-3 text-xs uppercase tracking-ultra text-neutral-500 hover:text-accent inline-flex items-center gap-1"
                  data-testid={`cart-remove-${item.product_id}`}
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <aside className="bg-white border border-black/10 p-8 h-fit" data-testid="cart-summary">
          <div className="text-xs uppercase tracking-ultra text-neutral-500 mb-6">Summary</div>
          <div className="flex justify-between text-sm py-2">
            <span>Subtotal</span>
            <span className="font-medium">{fmtPrice(total, business.currency)}</span>
          </div>
          <div className="flex justify-between text-sm py-2">
            <span>Shipping</span>
            <span className="text-neutral-500">Calculated at checkout</span>
          </div>
          <div className="border-t border-black/10 mt-4 pt-4 flex justify-between text-lg font-black">
            <span>Total</span>
            <span data-testid="cart-total">{fmtPrice(total, business.currency)}</span>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="mt-8 w-full bg-black text-white px-8 py-4 text-xs uppercase tracking-ultra font-bold hover:bg-neutral-800 transition-colors"
            data-testid="cart-checkout-button"
          >
            Checkout
          </button>
          <Link
            to="/shop"
            className="mt-3 block text-center text-xs uppercase tracking-ultra underline underline-offset-4"
          >
            Continue shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
